/**
 * Parsnip write operations — upload, analyze, status check.
 * Lives at hooks/parsnip/ (not querry_hooks/) — mirrors hooks/dashboard/useVideoGeneration.ts.
 * Directly imports and calls parsnipApi; no caching logic here.
 */

import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  uploadVideoWithSignedUrl,
  analyzePetReactions,
  getAnalysisStatus,
} from '@/api/parsnipApi';
import type { AnalyzeRequest, UploadProgress } from '@/api/parsnipApi';

// ─── Upload ───────────────────────────────────────────────────────────────────

interface UploadVideoParams {
  file: File;
  videoCategory: 'content' | 'result';
  onProgress?: (progress: UploadProgress) => void;
  batchId?: string;
}

export const useUploadParsnipVideo = () =>
  useMutation({
    mutationFn: ({ file, videoCategory, onProgress, batchId }: UploadVideoParams) =>
      uploadVideoWithSignedUrl(file, videoCategory, onProgress, batchId),
  });

// ─── Analyse ──────────────────────────────────────────────────────────────────

export const useAnalyzePetReactions = () =>
  useMutation({
    mutationFn: (request: AnalyzeRequest) => analyzePetReactions(request),
  });

// ─── Status check ─────────────────────────────────────────────────────────────
// useCallback (not useMutation) — called from polling intervals and WebSocket
// handlers, not as a one-shot user-triggered action.

export const useAnalysisStatusCheck = () =>
  useCallback(
    (analysisId: string) => getAnalysisStatus(analysisId),
    []
  );
